"use client";

import { useEffect, useRef, useState } from "react";
import "./tellus.css";

/* =========================================================
   REVEAL ANIMATION
========================================================= */

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -60px 0px",
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

/* =========================================================
   BUSINESS PROBLEMS
========================================================= */

const problems = [
  ["▥", "Revenue", "Growth"],
  ["◎", "Margin", "Improvement"],
  ["▣", "Cash &", "Working Capital"],
  ["⚙", "Cost", "Optimisation"],
  ["▰", "Supply Chain", "& Logistics"],
  ["🛒", "Procurement", "& Spend"],
  ["◇", "Inventory", "& Demand"],
  ["⌕", "Assets &", "Maintenance"],
  ["♙", "People &", "Productivity"],
  ["♙", "Customer &", "Channel"],
  ["☁", "Technology", "Modernisation"],
  ["AI", "Data & AI", "Enablement"],
  ["•••", "Something", "Else"],
];

/* =========================================================
   ROLES
========================================================= */

const roles = [
  "CEO",
  "CFO",
  "CIO / CTO",
  "CHRO",
  "COO",
  "Business Leader",
  "Other",
];

/* =========================================================
   FORM TYPE
========================================================= */

type FormData = {
  name: string;
  company: string;
  email: string;
  phone: string;
  country: string;
};

/* =========================================================
   PAGE
========================================================= */

export default function BusinessProblemPage() {
  /* REVEAL REFS */

  const problemGrid$ = useReveal<HTMLDivElement>();
  const roles$ = useReveal<HTMLDivElement>();
  const details$ = useReveal<HTMLElement>();
  const benefits$ = useReveal<HTMLElement>();
  const contact$ = useReveal<HTMLElement>();
  const cta$ = useReveal<HTMLElement>();

  /* SCROLL TO DETAILS SECTION FROM HASH */

  useEffect(() => {
    if (window.location.hash === "#tell-details") {
      setTimeout(() => {
        document.getElementById("tell-details")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 300);
    }
  }, []);

  /* STATE */

  const [selectedProblems, setSelectedProblems] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [challenge, setChallenge] = useState("");

  /* Only these 5 fields are submitted to DB. */

  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  /* SELECT / UNSELECT BUSINESS PROBLEM */

  const handleProblemSelect = (title: string, sub: string) => {
    const value = `${title} ${sub}`;

    setSelectedProblems((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      }

      return [...prev, value];
    });
  };

  /* INPUT HANDLER */

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
  };

  /* SUBMIT FORM — only name, company, email, phone, country are sent to API. */

  const handleSubmit = async () => {
    setError("");
    setMessage("");

    /* REQUIRED VALIDATION */

    if (!formData.name.trim()) {
      setError("Please enter your name.");
      return;
    }

    if (!formData.company.trim()) {
      setError("Please enter your company name.");
      return;
    }

    if (!formData.email.trim()) {
      setError("Please enter your work email.");
      return;
    }

    /* EMAIL VALIDATION */

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email.trim())) {
      setError("Please enter a valid work email.");
      return;
    }

    /* API REQUEST */

    try {
      setIsSubmitting(true);

      const response = await fetch("/api/tell-us", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          company: formData.company.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          country: formData.country.trim(),
        }),
      });

      const result = await response.json();

      /* API ERROR */

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Something went wrong.");
      }

      /* SUCCESS */

      const successMessage =
        result.message || "Thank you! We will get back to you soon.";

      setMessage(successMessage);
      setShowSuccessPopup(true);

      /* RESET FORM */

      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        country: "",
      });
    } catch (err) {
      console.error("Tell Us submission error:", err);

      setError(
        err instanceof Error
          ? err.message
          : "Unable to submit your request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /* CLOSE SUCCESS POPUP */

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
    setMessage("");
  };

  return (
    <main className="tell-page">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="tell-hero">
        <div className="tell-heroBg" />

        <div className="tell-heroCopy">
          <h1 className="tell-loadIn" style={{ animationDelay: "0.1s" }}>
            Tell Us Your
            <br />
            Business Problem.
            <br />
            <span>Let&apos;s Explore</span>
            <br />
            <span>What&apos;s Possible.</span>
          </h1>

          <div className="tell-loadIn" style={{ animationDelay: "0.28s" }}>
            Bring us your real business challenges.
            <br />
            Our experts will help you explore solutions with SAP,
            Data and AI.
          </div>
        </div>
      </section>

      {/* =====================================================
          FORM
      ===================================================== */}

      <section className="tell-form">

        {/* STEP 1 - BUSINESS PROBLEMS */}

        <div className="tell-section tell-step1">
          <div className="tell-heading">
            <div>
              <b>What are you trying to improve?</b>

              <small>
                Select one or more areas. (You can choose multiple)
              </small>
            </div>
          </div>

          <div
            className={`tell-problemGrid tell-reveal${
              problemGrid$.visible ? " tell-inView" : ""
            }`}
            ref={problemGrid$.ref}
          >
            {problems.map(([icon, title, sub], i) => {
              const value = `${title} ${sub}`;

              const isSelected = selectedProblems.includes(value);

              return (
                <button
                  key={title + sub}
                  type="button"
                  className={isSelected ? "selected" : ""}
                  style={{
                    transitionDelay: problemGrid$.visible
                      ? `${i * 0.035}s`
                      : "0s",
                  }}
                  onClick={() => handleProblemSelect(title, sub)}
                >
                  <em>{icon}</em>

                  <strong>
                    {title}
                    <br />
                    {sub}
                  </strong>
                </button>
              );
            })}
          </div>
        </div>

        {/* ROLE */}

        <div className="tell-section">
          <div className="tell-heading">
            <div>
              <b>What best describes you?</b>

              <small>
                This helps us connect you with the right team.
              </small>
            </div>
          </div>

          <div
            className={`tell-roles tell-reveal${
              roles$.visible ? " tell-inView" : ""
            }`}
            ref={roles$.ref}
          >
            {roles.map((role, i) => (
              <button
                key={role}
                type="button"
                className={selectedRole === role ? "selected" : ""}
                style={{
                  transitionDelay: roles$.visible ? `${i * 0.05}s` : "0s",
                }}
                onClick={() => setSelectedRole(role)}
              >
                <em>♙</em>

                <strong>{role}</strong>
              </button>
            ))}
          </div>
        </div>

        {/* DETAILS */}

        <div
          id="tell-details"
          className={`tell-details tell-reveal${
            details$.visible ? " tell-inView" : ""
          }`}
          ref={details$.ref}
        >

          {/* CHALLENGE */}

          <div className="tell-detailsPanel">
            <div>
              <div className="tell-heading">
                <div>
                  <b>Tell us what&apos;s happening.</b>

                  <small>
                    Share a few details about your business
                    challenge, goals or use case.
                  </small>
                </div>
              </div>

              <div className="tell-textarea">
                <textarea
                  maxLength={1000}
                  value={challenge}
                  onChange={(e) => setChallenge(e.target.value)}
                  placeholder="Type here... (e.g. We want to improve demand forecasting for our FMCG business across multiple plants using SAP data and AI...)"
                />

                <small>{challenge.length}/1000</small>
              </div>
            </div>
          </div>

          {/* CONTACT DETAILS */}

          <div className="tell-detailsPanel">
            <div>
              <div className="tell-heading">
                <div>
                  <b>Your details</b>

                  <small>So we can get back to you.</small>
                </div>
              </div>

              <div className="tell-inputs">

                {/* NAME */}

                <label>
                  Name *

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                  />
                </label>

                {/* COMPANY */}

                <label>
                  Company *

                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your company name"
                  />
                </label>

                {/* EMAIL */}

                <label>
                  Work Email *

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@company.com"
                  />
                </label>

                {/* PHONE */}

                <label>
                  Phone / Mobile

                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91 98765 43210"
                  />
                </label>

                {/* COUNTRY */}

                <label>
                  Country / Region

                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  >
                    <option value="" disabled>
                      Select country
                    </option>

                    <option value="India">India</option>

                    <option value="UAE">UAE</option>
                  </select>
                </label>

                {/* ERROR */}

                {error && <div className="tell-formError">{error}</div>}

                {/* SUBMIT */}

                <button
                  className="fp-buttons fp-btn-primary tell-submit"
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Request →"}
                </button>
              </div>

              <small className="tell-privacy">
                🔒 We respect your privacy. Your information is
                secure with us.
              </small>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          BENEFITS
      ===================================================== */}

      <section
        className={`tell-benefits tell-reveal${
          benefits$.visible ? " tell-inView" : ""
        }`}
        ref={benefits$.ref}
      >
        {[
          ["ϟ", "Quick Response", "We'll get back within 1 business day."],
          ["♙", "Relevant Experts", "Connect with the right team."],
          ["◎", "Practical Discussion", "Focus on outcomes, not just technology."],
          ["♢", "No Obligation", "A conversation, not a sales pitch."],
        ].map(([icon, title, text], i) => (
          <div
            key={title}
            style={{
              transitionDelay: benefits$.visible ? `${i * 0.07}s` : "0s",
            }}
          >
            <em>{icon}</em>

            <p>
              <b>{title}</b>

              <small>{text}</small>
            </p>
          </div>
        ))}
      </section>

      {/* =====================================================
          CONTACT
      ===================================================== */}

      <section
        className={`tell-contact tell-reveal${
          contact$.visible ? " tell-inView" : ""
        }`}
        ref={contact$.ref}
      >

        {/* OFFICES */}

        <div className="tell-office">
          <div className="tell-building" />

          <div className="tell-officeText">
            <h2>
              Our Offices
              <br />

              <strong>
                Global Delivery.
                <br />
                Local Understanding.
              </strong>
            </h2>

            {[
              ["Noida (India)", "QAIPL Tower, A-6, Sector 68, Noida 201301"],
              ["Bengaluru (India)", "Manyata Tech Park, Bengaluru"],
              ["Addis Ababa (Ethiopia)", "Bole, Addis Ababa"],
              ["Nairobi (Kenya)", "Westlands, Nairobi"],
              ["Dubai (UAE)", "Business Bay, Dubai"],
            ].map(([city, address]) => (
              <div className="tell-loc" key={city}>
                <i>●</i>

                <p>
                  <b>{city}</b>

                  <small>{address}</small>
                </p>
              </div>
            ))}

            <a href="#locations">View All Locations →</a>
          </div>
        </div>

        {/* CONTACT CARD */}

        <div className="tell-contactCard">
          <h2>Contact Us Directly</h2>

          <p>Prefer to reach out? Here are the right people.</p>

          {[
            ["General Inquiries", "info@orane.com"],
            ["Sales & New Business", "sales@orane.com"],
            ["Client Support", "support@orane.com"],
            ["Partnerships & Alliances", "partners@orane.com"],
            ["Careers", "careers@orane.com"],
            ["Call Us", "+91 120 456 7800"],
          ].map(([title, value]) => (
            <div className="tell-contactRow" key={title}>
              <i>♧</i>

              <p>
                <b>{title}</b>

                <a
                  href={
                    value.includes("@")
                      ? `mailto:${value}`
                      : `tel:${value.replace(/\s+/g, "")}`
                  }
                >
                  {value}
                </a>
              </p>
            </div>
          ))}
        </div>

        {/* GLOBAL */}

        <div className="tell-global">
          <h2>Global Presence</h2>

          <p>Supporting clients across 6 continents.</p>

          <div className="tell-map">
            <img src="/assets/images/map11.jpg" alt="Global presence map" />
          </div>

          <div className="tell-globalStats">
            <div>
              <b>20+</b>
              <small>Countries</small>
            </div>

            <div>
              <b>400+</b>
              <small>Minds</small>
            </div>

            <div>
              <b>Global</b>
              <small>Delivery Model</small>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section
        className={`tell-cta tell-reveal${cta$.visible ? " tell-inView" : ""}`}
        ref={cta$.ref}
      >
        <div className="tell-ctaCopy">
          <h2>Real Problems. Real People. Real Progress.</h2>

          <p>
            Start a conversation today and take the next
            step towards a more valuable tomorrow.
          </p>

          <div className="tell-ctaActions">
            <a href="#tell-details" className="fp-buttons fp-btn-primary tell-ctaBtn">
              Tell Us Your Problem →
            </a>
          </div>
        </div>
      </section>

      {/* =====================================================
          SUCCESS POPUP
      ===================================================== */}

      {showSuccessPopup && (
        <div className="tell-popupOverlay">
          <div
            className="tell-successPopup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tell-success-title"
          >

            <button
              type="button"
              className="tell-popupClose"
              onClick={closeSuccessPopup}
              aria-label="Close success message"
            >
              ×
            </button>

            <div className="tell-successIcon">✓</div>

            <h3 id="tell-success-title">Request Submitted!</h3>

            <p>
              {message || "Thank you! We will get back to you soon."}
            </p>

            <button
              type="button"
              className="fp-buttons fp-btn-primary tell-popupButton"
              onClick={closeSuccessPopup}
            >
              Done
            </button>

          </div>
        </div>
      )}

    </main>
  );
}